// The event part of the composer (3a R4; Figma eWr6sqSGfDufrRNUMjbtK4 web composer · LIVE on 2762:1307,
// LIVE off 2972:12343): date + start (+ optional end), place name, Join me (pasted profile link, empty = me),
// price in € (empty = free), ticket link, LIVE switch. The place row opens the map picker (3b R7).
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, Clock, Euro, Map as MapIcon, MapPin, Radio, Ticket, User, X } from "lucide-react";
import { DEFAULT_MAP_CENTER } from "../../lib/mapDefaults";
import { useMapStore } from "../../store/mapStore";
import LocationPickerModal, { formatCoords } from "./LocationPickerModal";
import { effectiveEndDate, startDatePatch, type EventDraft, type EventField } from "./eventDraft";

interface Props {
  draft: EventDraft;
  onChange(patch: Partial<EventDraft>): void;
  errors: Partial<Record<EventField, true>>;
  /** The server refused the pasted Join me link (INVALID_FIELD). */
  joinMeRejected: boolean;
  /** An edit's Join me person when it is not the author: the prefilled link and their name. */
  // unavailable: the account is gone (name is empty); the link stays so a save keeps it.
  joinMeKnown: { url: string; name: string; unavailable?: boolean } | null;
}

const ROW = "flex items-center gap-3 rounded-lg bg-[var(--bg-elevated)] p-3 text-[var(--foreground)]";
const INPUT = "min-w-0 flex-1 bg-transparent text-base text-[var(--foreground)] outline-none placeholder:text-[var(--foreground-subtle)] disabled:cursor-not-allowed";
const LINK = "shrink-0 text-sm font-bold text-[var(--primary)] dark:text-[var(--primary-on-surface)] cursor-pointer";
const ICON = "shrink-0 text-[var(--foreground-muted)]";

function Row({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className={ROW}>
      {icon}
      {children}
    </div>
  );
}

function FieldError({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <p data-testid={testId} role="alert" className="-mt-1 text-xs text-[var(--destructive-text)]">
      {children}
    </p>
  );
}

export default function EventFields({ draft, onChange, errors, joinMeRejected, joinMeKnown }: Props) {
  const { t } = useTranslation();
  const [ticketOpen, setTicketOpen] = useState(draft.ticketUrl !== "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [joinMeOpen, setJoinMeOpen] = useState(draft.joinMeUrl !== "");
  const link = draft.joinMeUrl.trim();
  const joinMeText =
    link === "" ? t("composer_event_joinme_you", "Join me: you")
      : joinMeKnown?.unavailable && link === joinMeKnown.url ? t("event_join_me_unavailable", "Join me · unavailable")
      : joinMeKnown && link === joinMeKnown.url ? t("composer_event_joinme_name", { name: joinMeKnown.name, defaultValue: "Join me: {{name}}" })
        : t("composer_event_joinme_pasted", "Join me: the pasted profile");

  return (
    <div data-testid="composer-event-fields" className="flex flex-col gap-3">
      <Row icon={<CalendarDays size={22} className={ICON} aria-hidden />}>
        <input data-testid="composer-event-date" type="date" className={INPUT} aria-label={t("composer_event_date", "Date")}
          value={draft.date} onChange={(e) => onChange(startDatePatch(draft, e.target.value))} />
      </Row>
      <Row icon={<Clock size={22} className={ICON} aria-hidden />}>
        <input data-testid="composer-event-time" type="time" className={INPUT} aria-label={t("composer_event_start", "Start time")}
          value={draft.time} onChange={(e) => onChange({ time: e.target.value })} />
        <span className="shrink-0 text-xs text-[var(--foreground-muted)]">{t("composer_event_until", "until")}</span>
        <input data-testid="composer-event-end" type="time" className={INPUT} aria-label={t("composer_event_end", "End time (optional)")}
          value={draft.endTime} onChange={(e) => onChange({ endTime: e.target.value })} />
      </Row>
      {draft.endTime !== "" && (
        // The end's day: the start's (or the next one across midnight) until chosen; a multi-day event keeps its own.
        <Row icon={<CalendarDays size={22} className={ICON} aria-hidden />}>
          <span className="shrink-0 text-xs text-[var(--foreground-muted)]">{t("composer_event_ends_on", "Ends on")}</span>
          <input data-testid="composer-event-end-date" type="date" className={INPUT} aria-label={t("composer_event_end_date", "End date")}
            value={effectiveEndDate(draft)} onChange={(e) => onChange({ endDate: e.target.value })} />
        </Row>
      )}
      {errors.end && (
        <FieldError testId="composer-event-end-error">{t("composer_event_end_invalid", "The end must be after the start")}</FieldError>
      )}
      <p className="-mt-1 text-xs text-[var(--foreground-subtle)]">
        {t("composer_event_timezone", { timezone: draft.timezone, defaultValue: "Times in {{timezone}}" })}
      </p>

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className={`${ROW} sm:w-[260px] sm:shrink-0`}>
          {draft.coords ? (
            // Mini-map thumbnail: the pin on a grey well (tiles are not fetched for a 40 px chip).
            <span aria-hidden className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[var(--bg-surface)]">
              <MapPin size={20} className="text-[var(--primary)] dark:text-[var(--primary-on-surface)]" />
            </span>
          ) : (
            <MapPin size={22} className={ICON} aria-hidden />
          )}
          <span data-testid="composer-event-place-coords" className="min-w-0 flex-1 truncate text-base">
            {draft.coords ? formatCoords(draft.coords) : t("event_place_none")}
          </span>
          <button type="button" data-testid="composer-event-place-map" className={`${LINK} flex items-center gap-1`} onClick={() => setPickerOpen(true)}>
            <MapIcon size={18} aria-hidden />
            {t("event_place_map")}
          </button>
        </div>
        <div className={`${ROW} min-w-0 flex-1 ${draft.coords == null ? "opacity-60" : ""}`}>
          {/* A place name is stored with its map point. */}
          <input data-testid="composer-event-place" className={INPUT} aria-label={t("composer_event_place", "Place")}
            disabled={draft.coords == null} value={draft.placeName} maxLength={255}
            placeholder={t("composer_event_place", "Place")}
            onChange={(e) => onChange({ placeName: e.target.value })} />
        </div>
      </div>
      {draft.coords == null && (
        <p className="-mt-1 text-xs text-[var(--foreground-subtle)]">{t("event_place_hint")}</p>
      )}
      {pickerOpen && (
        <LocationPickerModal
          initial={draft.coords}
          fallbackCenter={useMapStore.getState().lastCenter ?? DEFAULT_MAP_CENTER}
          onConfirm={(coords) => { onChange({ coords }); setPickerOpen(false); }}
          onRemove={() => { onChange({ coords: null, placeName: "" }); setPickerOpen(false); }}
          onCancel={() => setPickerOpen(false)}
        />
      )}

      <Row icon={<User size={22} className={ICON} aria-hidden />}>
        <span data-testid="composer-event-joinme-text" className={`min-w-0 flex-1 truncate text-base ${joinMeKnown?.unavailable && link === joinMeKnown.url ? "text-[var(--foreground-muted)]" : ""}`}>{joinMeText}</span>
        {!joinMeOpen && (
          <button type="button" className={LINK} onClick={() => setJoinMeOpen(true)}>
            {t("composer_event_joinme_paste", "Paste profile link")}
          </button>
        )}
      </Row>
      {joinMeOpen && (
        <Row icon={<User size={22} className={ICON} aria-hidden />}>
          <input data-testid="composer-event-joinme" className={INPUT} inputMode="url" aria-label={t("composer_event_joinme_link", "Join me profile link")}
            placeholder={t("composer_event_joinme_placeholder", "Paste a profile link · empty = you")}
            value={draft.joinMeUrl} onChange={(e) => onChange({ joinMeUrl: e.target.value })} />
        </Row>
      )}
      {(errors.joinMe || joinMeRejected) && (
        <FieldError testId="composer-event-joinme-error">
          {t("composer_event_joinme_invalid", "This profile link doesn't work. Paste the link of a profile on this app.")}
        </FieldError>
      )}

      <Row icon={<Euro size={22} className={ICON} aria-hidden />}>
        <input data-testid="composer-event-price" className={INPUT} inputMode="decimal" aria-label={t("composer_event_price", "Price in euros")}
          placeholder={t("composer_event_price_placeholder", "Price · empty = free")}
          value={draft.price} onChange={(e) => onChange({ price: e.target.value })} />
      </Row>
      {errors.price && (
        <FieldError testId="composer-event-price-error">{t("composer_event_price_invalid", "Enter a price like 12,50")}</FieldError>
      )}

      {ticketOpen ? (
        <Row icon={<Ticket size={22} className={ICON} aria-hidden />}>
          <input data-testid="composer-event-ticket" className={INPUT} inputMode="url" aria-label={t("composer_event_ticket", "Ticket link")}
            placeholder="https://" value={draft.ticketUrl} onChange={(e) => onChange({ ticketUrl: e.target.value })} />
          <button type="button" className={LINK} aria-label={t("composer_event_ticket_remove", "Remove ticket link")}
            onClick={() => { onChange({ ticketUrl: "" }); setTicketOpen(false); }}>
            <X size={18} aria-hidden />
          </button>
        </Row>
      ) : (
        <button type="button" className={`${LINK} self-start`} onClick={() => setTicketOpen(true)}>
          {t("composer_event_ticket_add", "Add ticket link")}
        </button>
      )}
      {errors.ticket && (
        <FieldError testId="composer-event-ticket-error">{t("composer_event_ticket_invalid", "Paste a web link (https://…)")}</FieldError>
      )}

      <button
        type="button"
        data-testid="composer-event-live"
        aria-pressed={draft.live}
        onClick={() => onChange({ live: !draft.live })}
        className={`flex w-full items-center gap-2 rounded-full px-4 py-3 text-left text-base font-bold cursor-pointer ${
          draft.live ? "bg-[var(--destructive)] text-[var(--primary-foreground)]" : "bg-[var(--bg-elevated)] text-[var(--foreground-muted)]"
        }`}
      >
        <Radio size={22} aria-hidden />
        {draft.live ? t("composer_event_live_on", "LIVE · this event will stream") : t("event_live", "LIVE")}
      </button>
    </div>
  );
}
